
import { Canvas } from "fabric";
import { cn } from "@/lib/utils";
import { Tool } from "@/hooks/use-canvas-setup";
import { ReactNode, useState, useEffect } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { createShape } from "../utils/shape-utils";
import { IncisionTemplateItem } from "../components/IncisionTemplateItem";
import { Circle, Square, Minus } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { CustomCanvas } from "@/types/fabric";
import { toggleMeasurementsVisibility } from "../utils/group-utils";

interface ShapesToolsProps {
  canvas: Canvas | null;
  onToolToggle: (tool: Tool) => void;
  icon: ReactNode;
  label: string;
}

export const ShapesTools = ({ canvas, onToolToggle, icon, label }: ShapesToolsProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showMeasurements, setShowMeasurements] = useState(true);

  // Initialize the showMeasurements state from canvas data
  useEffect(() => {
    if (canvas) {
      const customCanvas = canvas as CustomCanvas;
      // If showMeasurements is undefined, default to true
      setShowMeasurements(customCanvas.data?.showMeasurements !== false);
    }
  }, [canvas]);

  const handleShapeAdd = (shapeType: 'square' | 'circle' | 'rhomboid' | 'rectangle' | 'oval' | 'line') => {
    if (!canvas) return;

    const shape = createShape(canvas, shapeType);
    if (shape) {
      canvas.renderAll();
      onToolToggle('transform');
      
      // Trigger a custom event to notify that an incision template was added
      (canvas as any).fire('incision:template:added', { shape });
      
      // Close the dropdown after selection
      setIsOpen(false);
    }
  };

  const handleToggleMeasurements = (checked: boolean) => {
    if (!canvas) return;
    
    setShowMeasurements(checked);
    toggleMeasurementsVisibility(canvas as CustomCanvas, checked);
  };

  return (
    <div className="toolbar-item">
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <button
            className="modern-toolbar-button"
            aria-label={label}
          >
            {icon}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="center"
          className="modern-toolbar-dropdown w-64 p-2"
        >
          <div className="flex items-center justify-between px-2 py-1.5">
            <DropdownMenuLabel className="text-xs font-medium text-gray-600 m-0 p-0">
              Shape Templates
            </DropdownMenuLabel>
            <div className="flex items-center space-x-1.5">
              <Label htmlFor="show-measurements" className="text-[11px] text-gray-500 font-normal cursor-pointer whitespace-nowrap">
                Show mm
              </Label>
              <Switch 
                id="show-measurements" 
                checked={showMeasurements}
                onCheckedChange={handleToggleMeasurements}
                className="data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-gray-200"
              />
            </div>
          </div>
          
          <DropdownMenuSeparator className="mb-1.5" />
          <div className="grid grid-cols-2 gap-1.5 p-1">
            <IncisionTemplateItem 
              icon={Square}
              label="Square"
              onClick={() => handleShapeAdd('square')}
            />
            <IncisionTemplateItem 
              icon={Circle}
              label="Circle"
              onClick={() => handleShapeAdd('circle')}
            />
            <IncisionTemplateItem 
              icon={Square}
              label="Rhomboid"
              onClick={() => handleShapeAdd('rhomboid')}
            />
            <IncisionTemplateItem 
              icon={Square}
              label="Rectangle"
              onClick={() => handleShapeAdd('rectangle')}
            />
            <IncisionTemplateItem 
              icon={Circle}
              label="Oval"
              onClick={() => handleShapeAdd('oval')}
            />
            <IncisionTemplateItem 
              icon={Minus}
              label="Line"
              onClick={() => handleShapeAdd('line')}
            />
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
